import { useCallback, useEffect } from 'react'

import { Navigate } from 'react-router'

import {
  Post,
  PostStatus,
  PostTypeContext,
} from '@tribeplatform/gql-client/types'
import { findPostType, useCMS } from '@tribeplatform/react-components/CMS'
import { EntityImage } from '@tribeplatform/react-components/common/components'
import { HIGHLIGHTED_POST_QUERY_PARAM } from '@tribeplatform/react-components/common/constants'
import {
  useLogin,
  useQuery,
} from '@tribeplatform/react-components/common/hooks'
import { getPostIdFromPath } from '@tribeplatform/react-components/common/utils'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { PostForm } from '@tribeplatform/react-components/PostForm'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useAuthMember,
  useNetwork,
  usePost,
  useSpace,
} from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { BC } from '../../types/index.js'

import { StatusBadge } from './StatusBadge.js'
import {
  getTranslatedUpdatePostText,
  getTranslatedCreatePostText,
  getTranslatedPostTypeName,
} from './translations.js'
import { ComposerProps } from './types.js'

interface ComposerQueryParams {
  ['post_type']: string
  view: string
  ['space_id']: string
}
export const ComposerBlock: BC<ComposerProps> = ({
  new: isNew,
  spacePath,
  postPath,
  postTypeSlug: _,
  ...rest
}) => {
  const intl = useI18n()
  const { composablePostTypes } = useCMS()
  const { Link, push, replace, location } = useRouter()
  const { post_type: postTypeSlug, space_id: spaceId } =
    useQuery<ComposerQueryParams>()

  const {
    data: { id: networkId, postTypes: networkPostTypes },
  } = useNetwork()

  const postId = postPath ? getPostIdFromPath(postPath) : null
  const { data: post, isFetched } = usePost({
    variables: { id: postId },
    useQueryOptions: { enabled: !!postId },
  })

  const postType = findPostType({
    networkId,
    id: post?.postTypeId,
    slug: postTypeSlug,
    postTypes: networkPostTypes,
  })

  const defaultTargetSpace = postType?.authMemberProps?.availableSpaces?.[0]
  const defaultTargetSpaceExists = !!defaultTargetSpace
  const isSpaceSet = !!spacePath || !!spaceId
  const spaceQueryVariablesIfSet = spaceId
    ? { id: spaceId }
    : { path: spacePath }
  const spaceQueryVariablesIfNotSet = { id: defaultTargetSpace?.id }
  const { data: space } = useSpace({
    fields: {
      authMemberProps: {
        availablePostTypes: 'all',
        spaceMember: {
          role: 'basic',
        },
        permissions: 'all',
      },
      image: 'all',
    },
    variables: isSpaceSet
      ? spaceQueryVariablesIfSet
      : spaceQueryVariablesIfNotSet,
    useQueryOptions: { enabled: isSpaceSet || defaultTargetSpaceExists },
  })

  const { isGuest } = useAuthMember()
  const { showLogin } = useLogin()

  let availablePostTypes = []

  if (isSpaceSet) {
    availablePostTypes = space?.authMemberProps?.availablePostTypes?.filter(
      postType => postType?.context === PostTypeContext.post,
    )
  } else {
    availablePostTypes = networkPostTypes?.filter(
      postType =>
        postType?.context === PostTypeContext.post &&
        postType?.slug !== 'tribe-message' &&
        postType?.authMemberProps?.availableSpaces?.length > 0 &&
        !postType?.archived,
    )
  }

  const onCancel = () => {
    if (location?.key) {
      window?.history?.back()
    } else {
      push(isNew ? space.relativeUrl || '/' : post?.relativeUrl)
    }
  }

  useEffect(() => {
    if (isGuest) {
      showLogin(true)
    }
  }, [isGuest, showLogin])

  const onPostSubmitted = useCallback(
    (post: Post) => {
      if (post?.status === PostStatus.DRAFT && !!post?.scheduledFor) {
        replace(
          `/settings/moderation/all/scheduled-posts?${HIGHLIGHTED_POST_QUERY_PARAM}=${post?.id}`,
        )
      } else if (post?.status === PostStatus.DRAFT) {
        replace('/settings/moderation/all/draft-posts')
      } else {
        replace(post?.relativeUrl)
      }
    },
    [replace],
  )

  if (isGuest) {
    return null
  }

  if (composablePostTypes.length === 0 && !!isNew) {
    return <Navigate to="/" replace />
  }

  if (isNew && !postTypeSlug) {
    if (availablePostTypes?.length === 1) {
      replace(
        `${isSpaceSet ? `/${space?.slug}` : ''}/new?post_type=${
          availablePostTypes[0].id
        }`,
      )
      return null
    }
    return (
      <Card {...rest}>
        <Card.Header
          title={
            <div className="flex space-s-3 items-center">
              <IconButton
                variant="tertiaryNeutral"
                rounded
                size="lg"
                icon={
                  <SvgIcon className="rtl:-scale-x-100" name="arrow-left" />
                }
                aria-label={intl.$t({
                  id: 'Generics.Back',
                  defaultMessage: 'Back',
                })}
                onClick={onCancel}
              ></IconButton>
              <span className="text-xl flex-1">
                <T
                  id="Blocks.Composer.CreateButtonText"
                  defaultMessage="Create a new"
                />
              </span>
            </div>
          }
        />
        <Card.Content>
          <List divider spacing="none" className="border-t border-line-subdued">
            {availablePostTypes?.map(postType => (
              <List.Item key={postType?.id}>
                <Link
                  variant="inherit"
                  className="hover:bg-surface-hovered px-4 py-5 sm:p-6 flex space-s-3 items-center"
                  href={`${isSpaceSet ? `/${space?.slug}` : ''}/new?post_type=${
                    postType.id
                  }`}
                >
                  <EntityImage
                    size="xl"
                    entity={{ image: postType.icon, name: postType?.name }}
                  />
                  <span className="flex-1">
                    {getTranslatedPostTypeName(intl, postType.name)}
                  </span>
                  <SvgIcon
                    className="w-6 h-6 rtl:-scale-x-100"
                    name="chevron-right"
                  />
                </Link>
              </List.Item>
            ))}
          </List>
        </Card.Content>
      </Card>
    )
  }
  if (!isNew && !post) {
    return null
  }

  const postTypeTitle = post?.id
    ? getTranslatedUpdatePostText(intl, postType?.name)
    : getTranslatedCreatePostText(intl, postType?.name)

  return (
    <Card {...rest}>
      <Card.Header
        title={
          <div className="flex space-s-3 items-center">
            <IconButton
              variant="tertiaryNeutral"
              rounded
              size="lg"
              icon={<SvgIcon className="rtl:-scale-x-100" name="arrow-left" />}
              onClick={onCancel}
              aria-label={intl.$t({
                id: 'Generics.Back',
                defaultMessage: 'Back',
              })}
            />
            <span className="text-xl font-medium flex-1">{postTypeTitle}</span>
            <StatusBadge
              status={post?.status}
              scheduledFor={post?.scheduledFor}
            />
          </div>
        }
      />
      <Card.Content>
        <PostForm
          postType={postType}
          space={space}
          availableSpaces={postType?.authMemberProps?.availableSpaces}
          post={post}
          isPostReady={isFetched}
          onCancel={onCancel}
          onSubmit={onPostSubmitted}
        />
      </Card.Content>
    </Card>
  )
}
